<script lang="ts">
import {RouteNames} from "@/utils/constants.ts";

export function getRouteXAccountsCrypto() {
  return {
    name: RouteNames.X_ACCOUNTS_CRYPTO,
  };
}
</script>
<script setup lang="ts">
import BreadCrumbs from "@/components/BreadCrumbs.vue";
import TransactionsTableWidget from "@/widgets/TransactionsTableWidget.vue";
import BasicCard from "@/components/BasicCard.vue";
import MButton from "@/components/buttons/MButton.vue";
import CryptoTotalBalanceWidget from "@/widgets/crypto/CryptoTotalBalanceWidget.vue";
import IconTopRight from "@/components/icons/IconTopRight.vue";
import {getRouteXAccountsCryptoSend} from "@/views/Auth/accounts/crypto/SendCrypto.vue";
import {getRouteXAccountsCryptoBuy} from "@/views/Auth/accounts/crypto/BuyCrypto.vue";
import {getRouteXAccountsCryptoExchange} from "@/views/Auth/accounts/crypto/ExchangeCrypto.vue";
import { IconChevronDown } from "@iconify-prerendered/vue-carbon";
import { ref, computed, onMounted, onUnmounted } from "vue";

const selectedCurrency = ref("EUR");
const showCurrencyMenu = ref(false);

const currencies = [
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "CHF", symbol: "₣", name: "Swiss Franc" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "CNY", symbol: "¥", name: "Chinese Yuan" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "SEK", symbol: "kr", name: "Swedish Krona" },
  { code: "NOK", symbol: "kr", name: "Norwegian Krone" }
];

const currentCurrency = computed(() => {
  return currencies.find(c => c.code === selectedCurrency.value) || currencies[0];
});

const selectCurrency = (code: string) => {
  selectedCurrency.value = code;
  showCurrencyMenu.value = false;
};

// Close menu on outside click
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest('.currency-selector')) {
    showCurrencyMenu.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="flex flex-col gap-4 md:gap-6 px-4 md:px-0">
    <BreadCrumbs :title="$t('Crypto Wallets')">
      <template #buttons>
        <div class="relative currency-selector">
          <div
            @click="showCurrencyMenu = !showCurrencyMenu"
            class="flex items-center gap-2 px-3 py-2 bg-gray-800/30 rounded-lg border border-gray-700/50 cursor-pointer hover:border-gray-600 transition-colors"
          >
            <div class="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
              {{ currentCurrency.symbol }}
            </div>
            <span class="text-sm font-medium text-white">{{ currentCurrency.code }}</span>
            <IconChevronDown class="w-4 h-4 text-gray-400" />
          </div>

          <!-- Dropdown Menu -->
          <div
            v-if="showCurrencyMenu"
            class="absolute right-0 mt-2 w-56 bg-gray-800 rounded-lg shadow-lg border border-gray-700/50 py-2 z-50 max-h-80 overflow-y-auto"
          >
            <div
              v-for="currency in currencies"
              :key="currency.code"
              @click="selectCurrency(currency.code)"
              :class="[
                'px-4 py-2 cursor-pointer hover:bg-gray-700/50 transition-colors flex items-center justify-between',
                selectedCurrency === currency.code ? 'bg-gray-700/30' : ''
              ]"
            >
              <div class="flex items-center gap-3">
                <div class="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
                  {{ currency.symbol }}
                </div>
                <div>
                  <div class="text-sm font-medium text-white">{{ currency.code }}</div>
                  <div class="text-xs text-gray-400">{{ currency.name }}</div>
                </div>
              </div>
              <div v-if="selectedCurrency === currency.code" class="w-2 h-2 rounded-full bg-blue-600"></div>
            </div>
          </div>
        </div>
      </template>
    </BreadCrumbs>

    <div class="flex flex-col lg:flex-row gap-4 md:gap-6">
      <div class="w-full lg:w-[384px]">
        <CryptoTotalBalanceWidget class="">
          Total balance
        </CryptoTotalBalanceWidget>
      </div>
      <div class="flex-grow">
        <BasicCard title="Transfers" class="justify-between">
          <div class="flex flex-col sm:flex-row gap-3 md:gap-4">
            <MButton
                kind="secondary"
                size2="medium"
                :to="getRouteXAccountsCryptoSend()"
                class="w-full sm:w-auto"
            >
              Send Crypto
              <IconTopRight/>
            </MButton>
            <MButton
                kind="secondary"
                size2="medium"
                :to="getRouteXAccountsCryptoBuy()"
                class="w-full sm:w-auto"
            >
              Receive Crypto
            </MButton>
            <MButton
                kind="default"
                size2="medium"
                :to="getRouteXAccountsCryptoExchange()"
                class="w-full sm:w-auto"
            >
              Exchange
            </MButton>
          </div>
        </BasicCard>
      </div>
    </div>

    <BasicCard title="Portfolio">
      <TransactionsTableWidget/>
    </BasicCard>
  </div>

</template>

<style scoped lang="scss"></style>
